/**
 * OAuthResultPage - minimal popup close page for OAuth flows.
 *
 * The backend redirects the popup here (instead of the full integrations page)
 * after completing an OAuth install. This page:
 *  1. Reads the result from query params
 *  2. Broadcasts it to the parent window via BroadcastChannel
 *  3. Calls window.close()
 *
 * Using a dedicated page avoids the race between the full app mounting and
 * window.close(), and sidesteps Chrome clearing window.name on cross-origin
 * navigation (Slack sets COOP headers which also null window.opener).
 *
 * If window.close() is blocked (e.g. the user opened the URL directly),
 * a minimal message is shown so they can close the tab manually.
 */
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

export function OAuthResultPage() {
  const [searchParams] = useSearchParams();
  const [showFallback, setShowFallback] = React.useState(false);

  React.useEffect(() => {
    const provider = searchParams.get('provider') ?? 'unknown';
    const status = searchParams.get('status'); // 'connected' | 'error'
    const team = searchParams.get('team');
    const error = searchParams.get('error');

    // Normalize provider to UPPER_SNAKE_CASE for the message type
    // e.g. 'google-calendar' → 'GOOGLE_CALENDAR'
    const providerKey = provider.toUpperCase().replace(/-/g, '_');

    let message: Record<string, unknown>;
    if (status === 'connected') {
      message = { type: `${providerKey}_OAUTH_SUCCESS`, team };
    } else {
      message = { type: `${providerKey}_OAUTH_ERROR`, error: error ?? 'unknown' };
    }

    try {
      const bc = new BroadcastChannel(`${provider}-oauth`);
      bc.postMessage(message);
      bc.close();
    } catch {
      // BroadcastChannel not available - nothing we can do
    }

    // Give the broadcast a tick to deliver before closing
    const t = setTimeout(() => {
      window.close();
      // If we're still here after 300ms, the close was blocked
      setTimeout(() => setShowFallback(true), 300);
    }, 50);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showFallback) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'sans-serif',
        gap: '12px',
        color: '#ccc',
        background: '#111',
      }}
    >
      <p style={{ margin: 0, fontSize: '15px' }}>Connection complete. You can close this tab.</p>
      <button
        type="button"
        onClick={() => window.close()}
        style={{
          padding: '6px 16px',
          borderRadius: '6px',
          border: '1px solid #555',
          background: '#222',
          color: '#eee',
          cursor: 'pointer',
          fontSize: '13px',
        }}
      >
        Close tab
      </button>
    </div>
  );
}
